// @ts-check

/**
 * @file Renders grouped timelog cards in the dashboard.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"
import TimelogsDashboardLogCard from "./log-card"
import {timelogId} from "./log-model"

/** @import Timelog from "../../../frontend-models/timelog" */
/** @import {ProjectedTimelogDashboardRow} from "../../../../../shared/projected-timelog-reporting-types.js" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardLogSectionData
 * @property {string} id Stable section identifier.
 * @property {string} label Section heading.
 * @property {(InstanceType<typeof Timelog> | ProjectedTimelogDashboardRow)[]} timelogs Timelogs in the section.
 */

/**
 * @typedef {object} TimelogsDashboardLogSectionProps
 * @property {(timelogId: string) => unknown} onDeletePress Delete callback.
 * @property {(timelogId: string) => unknown} onEditPress Edit callback.
 * @property {Record<string, string | string[] | undefined>} params Current route params (forwarded so chip links keep system-test query params).
 * @property {TimelogsDashboardLogSectionData} section Log grouping to render.
 */

/** @typedef {Record<string, never>} TimelogsDashboardLogSectionState */

/**
 * Displays one grouped section of timelog cards.
 * @augments {ShapeComponent<TimelogsDashboardLogSectionProps, TimelogsDashboardLogSectionState>}
 */
class TimelogsDashboardLogSection extends ShapeComponent {
  static propTypes = PropTypesExact({
    onDeletePress: PropTypes.func.isRequired,
    onEditPress: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    section: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      timelogs: PropTypes.arrayOf(PropTypes.object).isRequired
    }).isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the section heading and cards.
   * @returns {ReactNode} Log section content.
   */
  render() {
    const {section} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.section ||= {
          gap: 12
        }}
        testID={`timelogsDashboardScreen/logSection/${section.id}`}
      >
        <View
          style={styles.header ||= {
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between"
          }}
          testID={`timelogsDashboardScreen/logSection/${section.id}/header`}
        >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 1.4,
              textTransform: "uppercase"
            }}
            testID={`timelogsDashboardScreen/logSection/${section.id}/title`}
          >
            {section.label}
          </Text>
          <Text
            style={styles[`count-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID={`timelogsDashboardScreen/logSection/${section.id}/count`}
          >
            {section.timelogs.length}
          </Text>
        </View>
        <View
          style={styles.cards ||= {
            gap: 12
          }}
          testID={`timelogsDashboardScreen/logSection/${section.id}/cards`}
        >
          {section.timelogs.map((timelog) => (
            <TimelogsDashboardLogCard
              key={timelogId(timelog)}
              log={timelog}
              onDeletePress={this.p.onDeletePress}
              onEditPress={this.p.onEditPress}
              params={this.p.params}
            />
          ))}
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardLogSectionProps>} */
const TimelogsDashboardLogSectionComponent = memo(shapeComponent(TimelogsDashboardLogSection))

export default TimelogsDashboardLogSectionComponent
